// Generates a stable device fingerprint from hardware signals that are identical
// across all browsers on the same physical machine (Chrome, Firefox, incognito, etc.).
// The GPU renderer string from WebGL is the most reliable cross-browser hardware signal.

const STORAGE_KEY = '_kw_dv';

function getWebGLRenderer() {
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
    if (!gl) return '';
    const ext = gl.getExtension('WEBGL_debug_renderer_info');
    if (!ext) return '';
    return gl.getParameter(ext.UNMASKED_RENDERER_WEBGL) || '';
  } catch {
    return '';
  }
}

async function sha256(str) {
  // SubtleCrypto requires a secure context (HTTPS or localhost).
  // Fall back to FNV-1a when unavailable (e.g. mobile on local network HTTP).
  if (crypto?.subtle) {
    const data = new TextEncoder().encode(str);
    const buf  = await crypto.subtle.digest('SHA-256', data);
    return Array.from(new Uint8Array(buf))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('')
      .slice(0, 32);
  }
  // FNV-1a 64-bit (two 32-bit halves)
  let h1 = 0x811c9dc5, h2 = 0x811c9dc5;
  for (let i = 0; i < str.length; i++) {
    const c = str.charCodeAt(i);
    h1 ^= c; h1 = Math.imul(h1, 0x01000193) >>> 0;
    h2 ^= (c * 31); h2 = Math.imul(h2, 0x01000193) >>> 0;
  }
  return (h1.toString(16).padStart(8, '0') + h2.toString(16).padStart(8, '0')).repeat(2);
}

let _cached = null;

export async function getDeviceId() {
  if (_cached) return _cached;

  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored) {
    _cached = stored;
    return stored;
  }

  const signals = [
    getWebGLRenderer(),
    screen.width,
    screen.height,
    screen.colorDepth,
    navigator.hardwareConcurrency || '',
    navigator.deviceMemory || '',
    Intl.DateTimeFormat().resolvedOptions().timeZone,
    navigator.platform || '',
  ].join('|');

  const id = await sha256(signals);
  localStorage.setItem(STORAGE_KEY, id);
  _cached = id;
  return id;
}
